import type { User } from "../services/UserService";
import {
  getRetailerConfig,
  getUserStoresForRetailer as getRetailerUserStores,
  EXTRA_ONLINE_RETAILERS,
  isBonusRetailer,
  type RetailerType,
} from "../config/retailers";
import { hasPlusOrProPlan } from "./retailerStoreLimits";

/**
 * Gets the correct store property key from the user object based on the retailer
 * @param retailer - The current retailer ("walmart" or "home_depot" or "target")
 * @returns The property key that contains the user's stores for that retailer
 */
export function getUserStoresKey(retailer: RetailerType): keyof User {
  return getRetailerConfig(retailer).userProperties.storesKey;
}

/**
 * Gets the user's stores for a specific retailer
 * @param user - The user object
 * @param retailer - The current retailer
 * @returns Array of store IDs for the specified retailer
 */
export function getUserStoresForRetailer(
  user: User | null,
  retailer: RetailerType
): string[] {
  return getRetailerUserStores(user, retailer);
}

/**
 * Pads Home Depot store numbers with leading zero if they are 3 digits
 * @param storeNumber - The store number to pad
 * @returns Padded store number (4 digits for Home Depot, unchanged for others)
 */
export function padHomeDepotStoreNumber(storeNumber: string): string {
  // Only pad if it's a 3-digit number
  if (/^\d{3}$/.test(storeNumber)) {
    return storeNumber.padStart(4, "0");
  }
  return storeNumber;
}

/**
 * Normalizes store numbers for comparison (pads Home Depot 3-digit numbers)
 * @param storeNumber - The store number to normalize
 * @param retailer - The retailer type
 * @returns Normalized store number
 */
export function normalizeStoreNumber(
  storeNumber: string,
  retailer: RetailerType
): string {
  if (retailer === "home_depot") {
    return padHomeDepotStoreNumber(storeNumber);
  }
  return storeNumber;
}

const DAILY_STORE_CHANGE_MS = 24 * 60 * 60 * 1000;
const LIMITED_STORE_CHANGE_RETAILERS = new Set<RetailerType>([
  "walmart",
  "home_depot",
  "target",
  "dollar_general",
  "bestbuy",
  "costco",
  "lowes",
  "tractor_supply",
  "northern_tool",
  "samsclub",
  "ace_hardware",
]);

type StoreChangePolicy = {
  windowMs: number;
  maxChanges: number;
  description: string;
};

function configuredStoreCount(user: Partial<User> | null, retailer: RetailerType): number {
  if (!user) return 0;
  return new Set(
    getRetailerUserStores(user as User, retailer).map((store) => String(store).trim()).filter(Boolean)
  ).size;
}

function individualStoreChangeUnits(current: string[], next: string[]): number {
  const currentSet = new Set(current.map((store) => String(store).trim()).filter(Boolean));
  const nextSet = new Set(next.map((store) => String(store).trim()).filter(Boolean));
  let removed = 0;
  currentSet.forEach((store) => { if (!nextSet.has(store)) removed += 1; });
  let added = 0;
  nextSet.forEach((store) => { if (!currentSet.has(store)) added += 1; });
  return Math.max(removed, added);
}

function getStoreChangePolicy(
  user: Partial<User> | null,
  retailer: RetailerType,
  storeSlotOverride?: number
): StoreChangePolicy | null {
  if (!LIMITED_STORE_CHANGE_RETAILERS.has(retailer)) return null;
  if (user?.unlimitedStoreChanges) return null;
  const plans = (user?.retailers ?? []).map((plan) => plan.toLowerCase());
  const storeSlots = Math.max(1, storeSlotOverride ?? configuredStoreCount(user, retailer));
  if (hasPlusOrProPlan(plans)) return null;
  return {
    windowMs: DAILY_STORE_CHANGE_MS,
    maxChanges: storeSlots,
    description: "once per store every 24 hours",
  };
}

const lastStoreChangeKey: Partial<Record<RetailerType, keyof User>> = {
  walmart: "lastStoreChange",
  home_depot: "lastHomeDepotStoreChange",
  target: "lastTargetStoreChange",
  dollar_general: "lastDollarGeneralStoreChange",
  bestbuy: "lastBestBuyStoreChange",
  costco: "lastCostcoStoreChange",
  lowes: "lastLowesStoreChange",
  tractor_supply: "lastTractorSupplyStoreChange",
  northern_tool: "lastNorthernToolStoreChange",
  samsclub: "lastSamsClubStoreChange",
  ace_hardware: "lastAceHardwareStoreChange",
};

/** Returns the newest recorded store change, including history outside the active lock window. */
export function getLastStoreChangeForRetailer(
  user: Partial<User> | null,
  retailer: RetailerType
): number | null {
  if (!user) return null;

  const key = lastStoreChangeKey[retailer];
  const direct = key ? (user[key] as number | undefined) : undefined;
  const timestamps = [direct, ...(user.recentStoreChanges?.[retailer] ?? [])]
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value > 0);

  return timestamps.length > 0 ? Math.max(...timestamps) : null;
}

function pruneStoreChangeTimestamps(timestamps: number[], windowMs: number, now = Date.now()): number[] {
  const cutoff = now - windowMs;
  return timestamps.filter((ts) => ts > cutoff).sort((a, b) => a - b);
}

export function recentStoreChangesForUser(
  user: Partial<User> | null,
  retailer: RetailerType,
  now = Date.now()
): number[] {
  if (!user) return [];
  const policy = getStoreChangePolicy(user, retailer);
  const windowMs = policy?.windowMs ?? DAILY_STORE_CHANGE_MS;
  const fromHistory = user.recentStoreChanges?.[retailer] ?? [];
  if (fromHistory.length > 0) {
    return pruneStoreChangeTimestamps(fromHistory, windowMs, now);
  }
  const key = lastStoreChangeKey[retailer];
  const lastChange = key ? (user[key] as number | undefined) : undefined;
  if (lastChange && now - lastChange < windowMs) {
    return [lastChange];
  }
  return [];
}

/** Matches the server's tier-based store-change lock for non-admin users. */
export function getWeeklyStoreChangeLock(
  user: Pick<User, "allowMoreStores"> & Partial<User> | null,
  retailer: RetailerType
): { isLocked: true; nextAllowedDate: Date; limitDescription: string } | null {
  const policy = getStoreChangePolicy(user, retailer);
  if (!user || user.allowMoreStores || !policy) {
    return null;
  }
  const now = Date.now();
  const recent = recentStoreChangesForUser(user, retailer, now);
  if (recent.length < policy.maxChanges) return null;
  const nextAllowed = recent[recent.length - policy.maxChanges] + policy.windowMs;
  if (now >= nextAllowed) return null;
  return { isLocked: true, nextAllowedDate: new Date(nextAllowed), limitDescription: policy.description };
}

export type StoreChangeWarning = {
  nextAllowedDate: Date;
  limitDescription: string;
};

/** Describes the lock that will begin if the user confirms one more store change. */
export function getStoreChangeWarning(
  user: Pick<User, "allowMoreStores"> & Partial<User> | null,
  retailer: RetailerType,
  changeTime = Date.now(),
  nextStores?: string[]
): StoreChangeWarning | null {
  if (!user || user.allowMoreStores) return null;

  const changeUnits = nextStores
    ? individualStoreChangeUnits(getRetailerUserStores(user as User, retailer), nextStores)
    : 1;
  if (changeUnits === 0) return null;
  const nextStoreCount = nextStores
    ? new Set(nextStores.map((store) => String(store).trim()).filter(Boolean)).size
    : 0;
  const policy = getStoreChangePolicy(
    user,
    retailer,
    Math.max(configuredStoreCount(user, retailer), nextStoreCount)
  );
  if (!policy) return null;
  const afterChange = [
    ...recentStoreChangesForUser(user, retailer, changeTime),
    ...Array.from({ length: changeUnits }, () => changeTime),
  ].sort((a, b) => a - b);
  if (afterChange.length < policy.maxChanges) return null;

  const nextAllowed = afterChange[afterChange.length - policy.maxChanges] + policy.windowMs;

  return {
    nextAllowedDate: new Date(nextAllowed),
    limitDescription: policy.description,
  };
}

export function parseStoreChangeError(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const status = "status" in error ? (error as { status: number }).status : undefined;
  if (status !== 425) return null;
  try {
    const parsed = JSON.parse((error as Error).message);
    let message = parsed?.data?.error || parsed?.message;
    if (parsed?.data?.nextAllowedChange) {
      const date = new Date(parsed.data.nextAllowedChange).toLocaleString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
      message = `${message}. You can change stores again on ${date}.`;
    }
    return message || null;
  } catch {
    return null;
  }
}

export const STORE_CHANGE_LIMIT_RETAILERS: RetailerType[] = [
  "walmart",
  "home_depot",
  "target",
  "dollar_general",
  "bestbuy",
  "costco",
  "lowes",
  "tractor_supply",
  "northern_tool",
  "samsclub",
  "ace_hardware",
];

export function planRetailerFromString(plan: string): RetailerType | null {
  for (const retailer of [...STORE_CHANGE_LIMIT_RETAILERS].sort((a, b) => b.length - a.length)) {
    if (plan === retailer || plan.startsWith(`${retailer}_`)) return retailer;
  }
  return null;
}

export function getUserPlanRetailers(user: Pick<User, "retailers">): RetailerType[] {
  const set = new Set<RetailerType>();
  for (const plan of user.retailers ?? []) {
    const base = planRetailerFromString(plan);
    if (base) set.add(base);
  }
  return [...set];
}

function hasStoreChangeTracking(user: Partial<User>, retailer: RetailerType, now = Date.now()): boolean {
  if (LIMITED_STORE_CHANGE_RETAILERS.has(retailer)) {
    const policy = getStoreChangePolicy(user, retailer);
    return policy !== null && recentStoreChangesForUser(user, retailer, now).length >= policy.maxChanges;
  }
  return false;
}

/** Retailers where the user currently has an active store-change limit window. */
export function getActiveStoreChangeLimits(
  user: Pick<User, "retailers" | "allowMoreStores" | "freeTierEnabled"> & Partial<User>
): RetailerType[] {
  const candidates = getUserPlanRetailers(user);
  if (isFreeTier(user as User)) {
    candidates.push("walmart", "home_depot");
  }
  return [...new Set(candidates)].filter((retailer) => {
    if (user.allowMoreStores && LIMITED_STORE_CHANGE_RETAILERS.has(retailer)) return false;
    return hasStoreChangeTracking(user, retailer);
  });
}

/** Clears store-change timestamps/history for retailers with active limits. */
export function buildResetStoreChangeLimitsPayload(
  user: Pick<User, "retailers" | "allowMoreStores" | "freeTierEnabled"> & Partial<User>
): Partial<User> | null {
  const retailers = getActiveStoreChangeLimits(user);
  if (!retailers.length) return null;

  const payload: Record<string, unknown> = {};
  const recent = { ...(user.recentStoreChanges ?? {}) };
  for (const retailer of retailers) {
    delete recent[retailer];
    const key = lastStoreChangeKey[retailer];
    if (key) payload[key] = null;
  }
  payload.recentStoreChanges = recent;
  return payload as Partial<User>;
}

/**
 * Checks if a user is set up based on whether they have retailers configured
 * @param user - The user object
 * @returns True if user has retailers configured, false otherwise
 */
type FreeTierUserShape = Pick<User, "retailers"> &
  Partial<Pick<User, "freeTierEnabled" | "accountType" | "admin" | "retailerAccessOverrides">>;

const PUBLIC_ONLINE_FAVORITE_RETAILERS = new Set([
  "walmart",
  "home_depot",
  "lowes",
  "tractor_supply",
  "northern_tool",
  "ace_hardware",
  "target",
  "bestbuy",
  "costco",
  "amazon",
  "kohls",
  "woot",
  ...EXTRA_ONLINE_RETAILERS,
]);

export function isUserSetUp(user: FreeTierUserShape | null): boolean {
  if (!user) return false;
  if (isFreeTier(user)) return true;
  return !!(user.retailers && user.retailers.length > 0);
}

/**
 * Checks if user is on the free tier (no retailers)
 * @param user - The user object
 * @returns True if user has no retailers (free tier), false otherwise
 */
export function isFreeTier(user: FreeTierUserShape | null): boolean {
  if (!user) return false;
  if (user.retailers && user.retailers.length > 0) return false;
  // The API's authoritative legacy/default rule is entitlement-based: every
  // non-admin account without a paid retailer grant is a signed-in free
  // account. Explicit free flags are retained for compatibility, but are not
  // required for older or recently-cancelled users.
  return user.admin !== true;
}

/** Admin or users granted add-lead permission via admin panel */
export function canAddLead(user: Pick<User, "admin" | "allowAddLead"> | null | undefined): boolean {
  return !!(user?.admin || user?.allowAddLead);
}

/** Plan cap for store slots; when allowMoreStores, at least show/save as many slots as the user already has */
/** Home Depot penny items are a Plus-and-up feature: home_depot_plus and
 * home_depot_pro qualify (plus admins); home_depot_basic does not. Mirrors
 * user.HasHomeDepotPennyAccess on the server. */
export function hasHomeDepotPennyAccess(
  user: Pick<User, "admin" | "retailers"> | null | undefined
): boolean {
  if (!user) return false;
  if (user.admin === true) return true;
  const retailers = Array.isArray(user.retailers) ? user.retailers : [];
  return retailers.some((plan) => {
    const p = String(plan).toLowerCase();
    return p.startsWith("home_depot") && (p.includes("_plus") || p.includes("_pro"));
  });
}

/** Penny Beta: every Penny Pro account, plus the per-account flag; admins
 * always qualify. Mirrors user.HasPennyBetaAccess on the server. */
export function hasPennyBetaAccess(
  user: Pick<User, "admin" | "pennyBeta" | "godModeOriginalUserId" | "retailers"> | null | undefined
): boolean {
  return !!user && (user.admin === true || user.pennyBeta === true || !!user.godModeOriginalUserId || hasHomeDepotPennyAccess(user));
}

export function effectiveStoreSlotLimit(
  planLimit: number,
  filledCount: number,
  allowMoreStores?: boolean
): number {
  if (!allowMoreStores) return planLimit;
  return Math.max(planLimit, filledCount);
}

/**
 * The saved stores the user's plan still covers. A downgraded account (e.g.
 * Starter's 5 Walmart stores on a 3-store Plus plan) keeps the old list in its
 * profile, and the server rejects any save that carries more stores than the
 * plan allows, so editors show and save only the first `slotLimit` stores.
 */
export function allottedStoreIds(
  storeIds: string[],
  slotLimit: number,
  allowMoreStores?: boolean
): string[] {
  const filled = storeIds.filter((id) => id && String(id).trim() !== "");
  if (allowMoreStores || slotLimit <= 0) return filled;
  return filled.slice(0, slotLimit);
}

export function isBasicTier(user: User | null): boolean {
  if (!user) return false;
  if ((user.accountType || "").toLowerCase() === "basic") return true;
  return (user.retailers || []).some((retailer) => {
    const normalizedRetailer = retailer.toLowerCase();
    return normalizedRetailer.includes("_basic") || normalizedRetailer.includes("-basic");
  });
}

export function normalizeRetailerAccessSlug(retailer: string): string {
  const normalized = retailer.toLowerCase().trim().replace(/-/g, "_");
  if (normalized === "walmart_online") return "walmart";
  if (normalized === "target_online") return "target";
  return normalized;
}

/** Returns an explicit admin decision, or undefined when the plan still applies. */
export function getRetailerAccessOverride(
  user: Pick<User, "retailerAccessOverrides"> | null | undefined,
  retailer: string,
): boolean | undefined {
  if (!user?.retailerAccessOverrides) return undefined;
  return user.retailerAccessOverrides[normalizeRetailerAccessSlug(retailer)];
}

/** Mirrors the API's retailer entitlement resolver, including admin overrides. */
export function hasUserRetailerAccess(
  user: Pick<User, "retailers" | "retailerAccessOverrides" | "admin"> | null | undefined,
  retailer: string,
): boolean {
  if (!user) return false;
  const overridden = getRetailerAccessOverride(user, retailer);
  const plans = Array.isArray(user.retailers) ? user.retailers : [];
  if (user.admin) return plans.length > 0 ? overridden ?? true : true;
  if (plans.length === 0) return false;
  if (overridden !== undefined) return overridden;

  const normalized = normalizeRetailerAccessSlug(retailer);
  if (["tractor_supply", "northern_tool", "samsclub", "ace_hardware"].includes(normalized)) {
    return hasPlusOrProPlan(plans);
  }
  if (plans.some((plan) => {
    const value = plan.toLowerCase().trim();
    return value === normalized || value.startsWith(`${normalized}_`);
  })) {
    return true;
  }
  return isBonusRetailer(normalized) && hasPlusOrProPlan(plans);
}

/**
 * Mirrors the server-side `hasPlanForRetailer` check used by the favorites
 * endpoints. Returns true when the user has a paid plan that grants favorites
 * access for the requested retailer.
 *
 * - home_depot: any plan starting with "home_depot"
 * - target / bestbuy: any non-basic paid plan (pro/starter/enterprise)
 * - everything else: any retailer plan grants access
 */
export function hasFavoritesPlanAccess(
  user: FreeTierUserShape | null,
  retailer: string,
  allowFreeOnline = false
): boolean {
  if (!user) return false;
  const normalized = retailer
    .toLowerCase()
    .replace("walmart_online", "walmart")
    .replace("target_online", "target");
  const overridden = getRetailerAccessOverride(user, normalized);
  if (user.admin) return overridden ?? true;
  const isPublicOnlineRetailer = PUBLIC_ONLINE_FAVORITE_RETAILERS.has(normalized);
  const hasBaselineOnlineAccess = allowFreeOnline && isPublicOnlineRetailer;

  // Favorites are a paid-account feature. Free users can browse every public
  // online feed, but they cannot fetch, view, or mutate saved products.
  if (isFreeTier(user)) return false;
  if (overridden !== undefined) return overridden;
  // Paid access is monotonic across public online feeds.
  if (hasBaselineOnlineAccess) return true;

  const retailers = Array.isArray(user.retailers) ? user.retailers : [];

  if (normalized === "home_depot") {
    return retailers.some((plan) => plan.toLowerCase().includes("home_depot"));
  }

  if (normalized === "target" || normalized === "bestbuy" || normalized === "lowes" || normalized === "tractor_supply" || normalized === "northern_tool" || normalized === "samsclub" || normalized === "ace_hardware") {
    return hasPlusOrProPlan(retailers);
  }

  return retailers.length > 0;
}
